import { Injectable } from '@angular/core';
import {
	Category,
	TagVM,
} from '@components/html-editor/tags-definition-dialog/tags.interfaces';
import { capitalizeFirstWord, revertId } from '@core/util/string';
import { flatten } from '@core/util/tags';
import { Context, ContextCategories, MatterFileType } from '@models/matter';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import {
	TagMapping,
	TagsMapping,
	TagsMappingMap,
} from '@state/global/tags/mappings/tags-mapping';
import { CustomTags } from '@state/global/tags/tags.models';
import { TagsSelector } from '@state/global/tags/tags.selector';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { cloneDeep } from 'lodash';
import LogRocket from 'logrocket';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface FlattenedCustomTags {
	description: string;
	dummyValue: string;
	label: string;
	value: string;
	category?: string;
}
@Injectable({
	providedIn: 'root',
})
export class TagsService {
	@SelectSnapshot(SelectedMatter.matterFileType) matterFileType: MatterFileType;

	public helpers: string[] = [
		'default',
		'lowercase',
		'uppercase',
		'capital',
		'day',
		'month',
		'year',
	];

	@Select(TagsSelector.getTags)
	public tags$: Observable<CustomTags>;

	getContextTags(
		context: string,
		insideLoop?: boolean,
	): Observable<FlattenedCustomTags[]> {
		return this.tags$.pipe(
			map((originalCustomTags) => {
				const customTags = cloneDeep(originalCustomTags);
				const tags: FlattenedCustomTags[] = [];

				if (customTags) {
					// delete the things we want to hide
					const FilterByContext = (
						_customTags: CustomTags,
						parent: string,
					): CustomTags => {
						const filteredTags = {} as CustomTags;
						const iterate = (
							obj: Record<string, unknown> | CustomTags,
							path: string,
						) => {
							for (const [key, value] of Object.entries(obj)) {
								const newPath = path ? `${path}.${key}` : key;
								if (typeof value === 'object' && value !== null) {
									iterate(value, newPath);
								} else if (newPath.startsWith(parent)) {
									filteredTags[newPath] = value;
								}
							}
						};

						iterate(_customTags, '');

						return filteredTags;
					};

					const filteredTags = FilterByContext(customTags, `${context}.all.0`);

					const FlattenedTags = flatten(filteredTags);

					const uniqueLabels = new Set<string>();

					for (const tag in FlattenedTags) {
						if (Object.prototype.hasOwnProperty.call(FlattenedTags, tag)) {
							let _tag = tag
								.replace(`${context}.`, '')
								.replace(/\.[^.]+/, '.id');
							if (insideLoop) {
								const propertyName = _tag?.match(/\.id\.(.*)/)
									? _tag?.match(/\.id\.(.*)/)[1]
									: '';
								_tag = `this.${propertyName}`;
							}
							if (
								typeof FlattenedTags[tag] === 'string' ||
								typeof FlattenedTags[tag] === 'number' ||
								typeof FlattenedTags[tag] === 'boolean'
							) {
								const label = _tag.replace('.defaultValue', '');
								if (!uniqueLabels.has(label)) {
									uniqueLabels.add(label);
									if (
										TagDescriptions[tag]?.dummyValue ===
										'use business information'
									) {
										tags.push({
											label,
											value: `${FlattenedTags[tag]}`,
											description: `${TagDescriptions[tag]?.description}`,
											dummyValue: `${FlattenedTags[tag]}`,
										});
									} else {
										tags.push({
											label,
											value: `${FlattenedTags[tag]}`,
											description: `${TagDescriptions[tag]?.description}`,
											dummyValue: `${TagDescriptions[tag]?.dummyValue}`,
										});
									}
								}
							}
						}
					}
				}

				return tags;
			}),
		);
	}

	getTags(context?: Context): Observable<FlattenedCustomTags[]> {
		return this.tags$.pipe(
			map((originalCustomTags) => {
				const customTags = cloneDeep(originalCustomTags);
				const tags: FlattenedCustomTags[] = [];

				if (customTags) {
					// delete the things we want to hide
					delete customTags?.commission?.hasBalanceOwingOrExcessDepositPayable;
					delete customTags?.commission?.excessDepositRecipient;
					delete customTags?.statementOfAdjustmentsTableRows;

					delete customTags?.trustLedgerTableExtendedRows;
					delete customTags?.trustLedgerTableCondensedRows;

					// rome-ignore lint/suspicious/noExplicitAny: <explanation>
					const StripCollection = (
						parent: any,
						prop: string,
						ids: string[],
					): void => {
						if (ids) {
							const contextType = context ? Object.keys(context)[0] : undefined;
							const contextId = context
								? context[contextType]?.[0]?.referenceId
								: undefined;
							ids
								.filter((i) => i !== contextId)
								.forEach((id) => (parent[prop][id] = undefined));
						}
					};

					// remove arrays and the specific items they refer to
					StripCollection(
						customTags,
						'clients',
						customTags?.clients?.all?.map((client) => client.id),
					);

					delete customTags?.clients?.all;
					delete customTags?.clients?.estate;
					delete customTags?.clients?.powerOfAttorney;
					delete customTags?.clients?.unique;

					StripCollection(
						customTags,
						'debts',
						customTags?.debts?.all?.map((debt) => debt.id),
					);
					delete customTags?.debts?.all;

					StripCollection(
						customTags,
						'encumbrances',
						customTags?.encumbrances?.all?.map((encumbrance) => encumbrance.id),
					);
					delete customTags?.encumbrances?.all;
					delete customTags?.encumbrances?.liens;
					delete customTags?.encumbrances?.mortgages;
					delete customTags?.encumbrances?.securityNotices;

					customTags?.mortgages?.all?.forEach(
						(mortgage) => delete mortgage.sctNumberAsReg,
					); // remove deprecated tag
					StripCollection(
						customTags,
						'mortgages',
						customTags?.mortgages?.all?.map((mortgage) => mortgage.id),
					);
					delete customTags?.mortgages?.all;

					StripCollection(
						customTags?.otherParty,
						'purchasers',
						customTags?.otherParty?.purchasers?.all?.map(
							(purchaser) => purchaser.id,
						),
					);
					delete customTags?.otherParty?.purchasers?.all;
					StripCollection(
						customTags?.otherParty,
						'vendors',
						customTags?.otherParty?.vendors?.all?.map((vendor) => vendor.id),
					);
					delete customTags?.otherParty?.vendors?.all;

					StripCollection(
						customTags,
						'requisitions',
						customTags?.requisitions?.all?.map((requisition) => requisition.id),
					);
					delete customTags?.requisitions?.all;

					StripCollection(
						customTags,
						'undertakings',
						customTags?.undertakings?.all?.map((undertaking) => undertaking.id),
					);
					// rome-ignore lint/suspicious/noExplicitAny: <explanation>
					delete (customTags?.undertakings as any)?._original;

					// remove deprecated tags
					delete customTags?.business?.office;
					delete customTags?.longClosingDate;
					delete customTags?.longExecutingDate;
					delete customTags?.otherParty?.lawyer;
					delete customTags?.landTransferTax;
					delete customTags?.plural;
					delete customTags?.pronoun;
					delete customTags?.pronouns;
					delete customTags?.pronounExtension;
					delete customTags?.property?.condo?.corporation?.baseFee;
					delete customTags?.property?.condo?.corporation?.totalFees;
					delete customTags?.property?.condo?.corporation?.urgencyFee;
					delete customTags?.clients?.length;
					delete customTags?.otherParty?.purchasers?.length;
					delete customTags?.otherParty?.vendors?.length;
					delete customTags?.signatories?.length;
					delete customTags?.otherParty?.pronouns;
					delete customTags?.purchasers?.capacityOfOwnership?.shares;
					delete customTags?.property?.condoPlanNumbers;
					delete customTags?.excessDeposit;
					delete customTags?.commission?.balanceOwing;
					delete customTags?.commission?.grandTotal;
					delete customTags?.commission?.hstAmount;
					delete customTags?.commission?.payable;
					delete customTags?.commission?.percentage;
					delete customTags?.letterhead;

					// remove other tags
					delete customTags?.property?.legalDescription?.primary;
					if (customTags?.fileType?.sale) {
						delete customTags?.titleInsurance;
					}

					const FlattenedTags = flatten(customTags);

					for (const tag in FlattenedTags) {
						if (Object.prototype.hasOwnProperty.call(FlattenedTags, tag)) {
							let _tag = tag.replace('.defaultValue', '');
							const contextType = context ? Object.keys(context)[0] : undefined;
							const contextId = context
								? context[contextType]?.[0]?.referenceId
								: undefined;
							if (_tag.includes(`${contextType}.${contextId}`)) {
								_tag = revertId(_tag, context);
							}
							const currentTag = FlattenedTags[tag];
							if (['string', 'number', 'boolean'].includes(typeof currentTag)) {
								const tagObj: FlattenedCustomTags = {
									label: _tag,
									value: `${currentTag}`,
									description: `${TagDescriptions[tag]?.description}`,
									dummyValue: `${TagDescriptions[tag]?.dummyValue}`,
								};

								if (
									TagDescriptions[tag]?.dummyValue ===
									'use business information'
								) {
									tagObj.dummyValue = `${currentTag}`;
								}

								if (_tag.includes(`${contextType}.id`)) {
									tagObj.category = ContextCategories[Object.keys(context)[0]];
								}

								tags.push(tagObj);
							}
						}
					}
				}

				return tags;
			}),
		);
	}

	private hasLoggedDuplicates = false;

	getMappedTagsAndCategories(
		location: string,
		context?: Context,
	): Observable<Category[]> {
		return this.getTags(context).pipe(
			map((tags) => {
				const _tags = tags.map((x) => ({
					...x,
					id: x.label.replace(/\./g, ''),
					labelLowerCase: x.label.toLowerCase(),
					valueLowerCase: x.value?.toLowerCase(),
					tag: x.label,
					variations: this.helpers.map((y) => ({
						value: y,
						label: y === 'default' ? `{{${x.label}}}` : `{{${y} ${x.label}}}`,
					})),
				}));
				const tagMapping = TagsMapping;
				for (const tag of tags) {
					if (!TagsMappingMap.has(tag.label)) {
						tagMapping.push({
							tag: tag.label,
							categories: [tag.category || 'Other'],
							hideFromProduction: false,
							fileType: [
								MatterFileType.SALE,
								MatterFileType.PURCHASE,
								MatterFileType.REFINANCE,
							],
						});
					}
				}

				const seenTags = new Set();
				const duplicateMappings = []; // Store duplicate mappings here

				const filteredTagMapping = tagMapping.filter((mapping) => {
					// Skip this mapping if it's a duplicate
					if (seenTags.has(mapping.tag)) {
						if (!this.hasLoggedDuplicates) {
							duplicateMappings.push(mapping);
						}
						return false;
					}
					seenTags.add(mapping.tag);

					// Remove tags that are not in the tags list
					if (!tags.some((tag) => tag.label === mapping.tag)) {
						return false;
					}

					// If file type is set and location is not 'business', remove tags that are not in the file type list
					if (this.matterFileType && location !== 'business') {
						if (mapping.fileType) {
							if (!mapping.fileType.includes(this.matterFileType)) {
								return false;
							}
						} else {
							return true;
						}
					}

					// In production mode, remove tags that are set to hideFromProduction
					if (
						process.env.NODE_ENV === 'production' &&
						mapping.hideFromProduction
					) {
						return false;
					}

					return true;
				});

				if (!this.hasLoggedDuplicates && duplicateMappings.length > 0) {
					LogRocket.log('Duplicate mappings found: ', duplicateMappings);
					this.hasLoggedDuplicates = true;
				}

				return this.createCategories(_tags, filteredTagMapping);
			}),
		);
	}

	createCategories(tags: TagVM[], filteredMapping: TagMapping[]) {
		const AcronymCategoryNames = ['POA'];
		const getOrCreateCategory = (
			name: string,
			map: Map<string, Category>,
			ancestors: string[],
		) => {
			if (!map.has(name)) {
				map.set(name, {
					name,
					amount: 0,
					subCategory: [],
					tags: [],
					ancestors,
				});
			}
			const category = map.get(name);
			category.amount++;
			return category;
		};

		const rootCategoryMap = new Map<string, Category>();
		const allCategory: Category = {
			name: 'All',
			amount: filteredMapping.length,
			subCategory: [],
			tags: [],
			ancestors: [],
		};
		rootCategoryMap.set('All', allCategory);

		filteredMapping.forEach((mapping) => {
			const levels = mapping.categories.filter(Boolean);
			let currentCategory: Category | null = allCategory;
			let currentMap = rootCategoryMap;
			const ancestors: string[] = [];

			levels.forEach((level) => {
				const currentAncestors = [...ancestors]; // Clone the current ancestors array
				const category = getOrCreateCategory(
					level,
					currentMap,
					currentAncestors,
				);
				if (
					currentCategory &&
					!currentCategory.subCategory.some((c) => c.name === category.name)
				) {
					currentCategory.subCategory.push(category);
				}
				ancestors.push(
					AcronymCategoryNames.includes(level)
						? level
						: capitalizeFirstWord(level.toLowerCase()),
				); // Add the current level to the ancestor list for the next level
				currentCategory = category;
				currentMap = new Map<string, Category>(
					currentCategory.subCategory.map((subCat) => [subCat.name, subCat]),
				);
			});

			if (currentCategory) {
				const tagFromList = tags.find((tag) => tag.tag === mapping.tag);
				const tagFromMapping = TagsMappingMap.get(mapping.tag);
				if (tagFromList) {
					currentCategory.tags.push({
						tag: tagFromList.tag,
						variations: this.helpers.map((y) => ({
							value: y,
							label:
								y === 'default'
									? `{{${tagFromList.tag}}}`
									: `{{${y} ${tagFromList.tag}}}`,
						})),
						description: tagFromMapping?.description,
						value: tagFromList.value,
						dummyValue: tagFromMapping?.defaultValue || tagFromList.dummyValue,
						fileTypes: tagFromMapping?.fileType,
					});
				}
			}
		});

		const sortCategories = (categories: Category[]): Category[] => {
			return categories
				.sort((a, b) => {
					if (a.name.toLowerCase() === 'other') return 1;
					if (b.name.toLowerCase() === 'other') return -1;
					return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
				})
				.map((category) => {
					const newCategory = {
						...category,
						name: AcronymCategoryNames.includes(category.name)
							? category.name
							: capitalizeFirstWord(category.name.toLowerCase()),
					};

					if (newCategory.subCategory) {
						newCategory.subCategory = sortCategories(newCategory.subCategory);
					}
					return newCategory;
				});
		};

		const categories = Array.from(rootCategoryMap.values());
		return sortCategories(categories);
	}
}

const TagDescriptions = {
	adjustmentDate: {
		description: 'Date of adjustment as selected in Statement of Adjustments',
		dummyData: 'data',
	},
	'agreedSalePrice.formatted': {
		description:
			'The sale price as given in the Agreement of Purchase & Sale and entered during the APS intake',
	},
	appointmentDate: {
		description:
			'Date of appointment as selected in the Key dates modal in file Overview (short form)',
	},
	appointmentLongDate: {
		description:
			'Date of appointment as selected in the Key dates modal in file Overview (long form)',
	},
	'balanceDue.formatted': {
		description: 'Balance due on closing as seen in Statement of adjustments',
	},
	'balanceDue.lessDeductions.formatted': {
		description:
			'Balance due on closing, less any Deductions, as seen in Statement of adjustments',
	},
	'balanceDue.totalDeductions.formatted': {
		description:
			'Total deductions to balance due on closing, as seen in Statement of adjustments',
	},
	'balanceDue.deductionsType.formatted': {
		description:
			'Deduction types for balance due on closing, as seen in Statement of adjustments',
	},
	'business.address.city': {
		description: "Firm's city",
		dummyValue: 'use business information',
	},
	'business.addressBlock': {
		description: "Property address (block format) of the user's firm",
		dummyValue: 'use business information',
	},
	'business.addressLine': {
		description: "Property address (single line format) of the user's firm",
		dummyValue: 'use business information',
	},
	'business.name': {
		description: "Name of the user's firm",
		dummyValue: 'use business information',
	},
	'clerk.initials': {
		description: 'Initials of the clerk assigned to the file',
		dummyValue: 'MC',
	},
	'clerk.name': {
		description: 'Full name of the clerk assigned to the file',
		dummyValue: 'Michelle Clerk',
	},
	'clientBrokerage.addressBlock': {
		description: "Address of the seller's brokerage company",
	},
	'clientBrokerage.name': {
		description: "Name of the seller's or listing brokerage company",
		dummyValue: 'Seller Brokerage Inc.',
	},
	'clients.names': {
		description: 'A comma separated list of clients, full names, on file',
		dummyValue: 'John Seller',
	},
	'clients.lastNames': {
		description: 'Last names of the clients added to the file',
		dummyValue: 'Seller',
	},
	'clients.residentStatus': {
		description: 'Resident statuses of all clients on file',
		dummyValue: 'Assume resident',
	},
	closingDate: {
		description: 'closing date in the format of January 1st, 2022',
	},
	date: {
		description: "Today's date",
	},
	'deposits.formatted': {
		description: 'Total amount of deposit as is in Adjustments page',
	},
	'encumbrances.List': {
		description: 'List of encumbrances added in Encumbrances page',
	},
	'excessDeposit.formatted': {
		description: 'Excess deposit as shown on Commission page (if applicable)',
	},
	'executingAddress.city': {
		description:
			"City as entered in the Key dates modal in Overview as 'Vendor executing city'",
		dummyValue: 'Toronto',
	},
	'lawyer.initials': {
		description: 'Initials of the lawyer assigned to the file',
		dummyValue: 'JL',
	},
	'lawyer.name': {
		description: 'Full name of the lawyer assigned to the file',
		dummyValue: 'Jane Lawer',
	},
	letterhead: {
		description:
			'Letter head of the office as selected in the file details window in Overview',
		dummyValue: 'use business information',
	},
	'month closingDate': {
		description: 'Month of the closing date',
	},
	'mortgageDischargeFee.formatted': {
		description: "Firm's fee associated with mortgage discharge",
	},
	'otherParty.brokerage.broker.firstName': {
		description: 'First name of the other party real estate agent',
	},
	'otherParty.brokerage.broker.lastName': {
		description: 'Last name of the other party real estate agent',
	},
	'otherParty.brokerage.name': {
		description: 'Name of the other party brokerage firm',
	},
	'otherParty.lawfirm.lawyer.name': {
		description: "Name of the purchaser's (other party on a Sale) lawyer",
		dummyValue: 'Bob Lawyer',
	},
	'otherParty.lawfirm.name': {
		description: "Name of the purchaser's (other party on Sale) law firm",
		dummyValue: 'Law Corporation',
	},
	'otherParty.purchasers.lastNames': {
		description: 'Last names of the purchasers added to the file',
		dummyValue: 'Buyer',
	},
	'otherParty.purchasers.names': {
		description:
			'Comma separated list of full names of the purchasers added to the file',
		dummyValue: 'Lily Buyer',
	},
	'pronouns.AmAre': {
		description:
			'Shows Am or Are depending on number of clients added to the file',
	},
	'pronouns.IsAre': {
		description:
			'Shows Is or Are depending on number of clients added to the file',
	},
	'pronouns.IWe': {
		description:
			'Shows I or We depending on number of clients added to the file',
	},
	'pronouns.MeUs': {
		description:
			'Shows Me or Us depending on number of clients added to the file',
	},
	'pronouns.MyOur': {
		description:
			'Shows My or Our depending on number of clients added to the file',
	},
	'property.address.city': {
		description: 'City of the property selected on file',
	},
	'property.address.country': {
		description: 'Country of the property selected on file',
	},
	'property.address.province': {
		description: 'Province of the property selected on file',
	},
	'property.address.street': {
		description: 'Street of the property selected on file',
	},
	'property.address.unit': {
		description: 'Unit of the property selected on file (if applicable)',
	},
	'property.addressBlock': {
		description: 'Address of the property of the transaction in a block format',
	},
	'property.addressLine': {
		description:
			'Address of the property added to the transaction in one single line',
	},
	'property.lro.selected.name': {
		description: 'Name of the LRO selected for the property',
	},
	requisitionDate: {
		description:
			'title search completion date or requisition date as entered in the Key Dates modal in file Overview',
	},
	'salutations.reLineClients': {
		description:
			'In the format of “Your sale to” and is applicable for letters that are sent to the client directly',
	},
	'salutations.reLineCondensed': {
		description:
			'Shorter version of the RE line includes the property address and seller/buyer names',
	},
	'salutations.reLineGeneral': {
		description:
			'Longer version of the RE line includes file number and closing date along with the property address and seller/buyer names',
	},
	'signatureBlock.clients': {
		description: 'Signature lines along with names of the clients',
	},
	'signatureBlock.lawyer': {
		description: 'Signature line and name of the lawyer',
	},
	'signatureLine.clientsNoTitle': {
		description:
			'Signature lines of clients with replaced associated contacts for corporations and clients with representation (found in Declaration of possession)',
	},
	'signatureLine.consentingSpouse': {
		description:
			'Signature lines for consenting spouses as selected in the FLA option for each seller on file',
	},
	undertakingsList: {
		description:
			'List of undertakings given (encumbrances, bills/debts, or custom)',
	},
	'year closingDate': {
		description: 'Year of the closing date',
	},
};
